import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { WaitlistProvider } from "@/context/WaitlistContext";
import Preloader from "@/components/Preloader";
import Nav from "@/components/Nav";
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ProductPage from "@/pages/ProductPage";
import GiftingPage from "@/pages/GiftingPage";
import AboutPage from "@/pages/AboutPage";
import SocialsPage from "@/pages/SocialsPage";
import AccountPage from "@/pages/AccountPage";
import PaymentsPage from "@/pages/PaymentsPage";
import AdminPage from "@/pages/AdminPage";
import VotePage from "@/pages/VotePage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderPage from "@/pages/OrderPage";

const NavGate = () => (useLocation().pathname.startsWith("/backend") ? null : <Nav />);

const ScrollReset = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
        else el.scrollIntoView({ behavior: 'smooth' });
      }, 250);
      return () => clearTimeout(t);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
};

function App() {
  const [loaded, setLoaded] = useState(false);
  const onDone = useCallback(() => setLoaded(true), []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.14, smoothWheel: true, wheelMultiplier: 1 });
    window.__lenis = lenis;
    let rafId;
    const raf = (t) => { lenis.raf(t); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); window.__lenis = null; };
  }, []);

  return (
    <CartProvider>
      <WaitlistProvider>
      <BrowserRouter>
        <div className="bg-cream text-cocoa min-h-screen font-body">
          {!loaded && <Preloader onDone={onDone} />}
          <ScrollReset />
          <NavGate />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/shop" element={<Navigate to="/products" replace />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/gifting" element={<GiftingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/vote" element={<VotePage />} />
            <Route path="/socials" element={<SocialsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/backend" element={<AdminPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order/:orderId" element={<OrderPage />} />
          </Routes>
          <Toaster position="bottom-center" toastOptions={{ style: { background: '#2A1010', color: '#FFF3D6', border: '2px solid #F7B500', borderRadius: 999, fontFamily: 'Oswald', letterSpacing: '0.08em', fontWeight: 700 } }} />
        </div>
      </BrowserRouter>
      </WaitlistProvider>
    </CartProvider>
  );
}

export default App;
